// src/pages/accounting/AccountingPlus.jsx
// Balance Sheet · Profit & Loss · Chart of Accounts · Audit Trail · Lock date — all from the same
// accounting engine as the Journal (server utils/ledger.js), so every number agrees.
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import { exportStyledExcel, MONEY_FMT, TOTAL_FILL } from '../../utils/exportExcel';

const GREEN = '#2e9f86';
const money = (n) => { const v = Number(n) || 0; return `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const errOf = (e) => e.response?.data?.error || e.message;
const Spin = () => <div className="p-10 text-center text-gray-400">Loading…</div>;
const Card = ({ children, className = '' }) => <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>{children}</div>;

/* ── Trial balance with opening / closing ───────────────────────────── */
export function TrialBalanceFull({ params }) {
  const { data, isLoading } = useQuery({ queryKey: ['acc-tb2', params], queryFn: () => api.get('/api/accounting/trial-balance', { params }).then((r) => r.data) });
  if (isLoading) return <Spin />;
  const rows = data?.rows || [];
  const balanced = data && Math.abs((data.totalDebit || 0) - (data.totalCredit || 0)) < 0.02 && Math.abs(data.totalClosing || 0) < 0.02;
  const exportXls = () => exportStyledExcel({
    filename: `trial_balance_${params.date_from}_${params.date_to}`, sheetName: 'Trial Balance', title: 'Trial Balance (USD)',
    subtitle: `${params.date_from} — ${params.date_to}`,
    columns: [
      { header: 'Account', key: 'account', width: 40 },
      { header: 'Opening', key: 'opening', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Debit', key: 'debit', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Credit', key: 'credit', width: 15, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Closing', key: 'closing', width: 15, numFmt: MONEY_FMT, align: 'right' },
    ],
    rows: [...rows, { account: 'TOTAL', opening: data.totalOpening, debit: data.totalDebit, credit: data.totalCredit, closing: data.totalClosing, __bold: true, __fill: TOTAL_FILL }],
  });
  return (
    <div>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className={`text-sm font-semibold ${balanced ? 'text-emerald-600' : 'text-red-600'}`}>{balanced ? '✅ Balanced' : '⚠️ NOT balanced'} — Debit {money(data?.totalDebit)} / Credit {money(data?.totalCredit)}</span>
        <button onClick={exportXls} className="px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg">Export Excel</button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 720 }}>
          <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500"><tr>
            <th className="text-left px-4 py-2.5">Account</th><th className="text-right px-4 py-2.5">Opening</th><th className="text-right px-4 py-2.5">Debit</th><th className="text-right px-4 py-2.5">Credit</th><th className="text-right px-4 py-2.5">Closing</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {!rows.length && <tr><td colSpan={5} className="text-center text-gray-400 py-10">No activity.</td></tr>}
            {rows.map((a) => (
              <tr key={a.account} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-700">{a.account}</td>
                <td className="px-4 py-2 text-right text-gray-500">{a.opening ? money(a.opening) : '—'}</td>
                <td className="px-4 py-2 text-right">{a.debit ? money(a.debit) : '—'}</td>
                <td className="px-4 py-2 text-right">{a.credit ? money(a.credit) : '—'}</td>
                <td className={`px-4 py-2 text-right font-bold ${a.closing >= 0 ? 'text-gray-800' : 'text-red-500'}`}>{money(a.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-gray-400 mt-2">Debit balances are positive, credit balances negative. Income and expenses of earlier periods are closed into Retained Earnings.</p>
    </div>
  );
}

/* ── Balance sheet ──────────────────────────────────────────────────── */
export function BalanceSheetTab({ params }) {
  const qc = useQueryClient();
  const date = params.date_to;
  const { data, isLoading, refetch } = useQuery({ queryKey: ['acc-bs', date], queryFn: () => api.get('/api/accounting/balance-sheet', { params: { date } }).then((r) => r.data) });
  const [busy, setBusy] = useState(false);
  if (isLoading) return <Spin />;
  const bookOpeningStock = async () => {
    if (!window.confirm('Record the value of stock that has no accounting entry yet (opening stock)?')) return;
    setBusy(true);
    try { await api.post('/api/accounting/opening-stock'); await refetch(); qc.invalidateQueries(['acc-tb2']); }
    catch (e) { alert(errOf(e)); } finally { setBusy(false); }
  };
  const Section = ({ title, rows, total }) => (
    <Card className="p-4">
      <div className="font-bold text-gray-800 mb-2">{title}</div>
      {!rows.length && <div className="text-sm text-gray-400">—</div>}
      {rows.map((r) => <div key={r.account} className="flex justify-between text-sm py-1 border-b border-gray-50"><span className="text-gray-600">{r.account}</span><span className={r.amount < 0 ? 'text-red-500' : ''}>{money(r.amount)}</span></div>)}
      <div className="flex justify-between font-bold mt-2 pt-2 border-t"><span>Total</span><span>{money(total)}</span></div>
    </Card>
  );
  const ic = data.inventory_check;
  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <span className="text-sm text-gray-500">As of <b>{date}</b></span>
        <span className={`text-sm font-semibold ${data.balanced ? 'text-emerald-600' : 'text-red-600'}`}>{data.balanced ? '✅ Assets = Liabilities + Equity' : '⚠️ Not balanced'}</span>
      </div>
      {ic && Math.abs(ic.difference) >= 0.01 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl p-3 mb-3 flex items-center gap-3 flex-wrap">
          <span>Inventory in the accounts is {money(ic.in_accounts)} but the real stock at cost is {money(ic.real_stock_at_cost)} (difference {money(ic.difference)}) — usually stock that was typed in before the system recorded it.</span>
          <button disabled={busy} onClick={bookOpeningStock} className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-semibold">Record opening stock</button>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-3">
        <Section title="Assets" rows={data.assets} total={data.total_assets} />
        <Section title="Liabilities" rows={data.liabilities} total={data.total_liabilities} />
        <Section title="Equity" rows={data.equity} total={data.total_equity} />
      </div>
    </div>
  );
}

/* ── Official P&L (also used by Reports → P&L) ─────────────────────── */
export function ProfitLossView({ params, endpoint = '/api/accounting/pnl' }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['pnl', endpoint, params], queryFn: () => api.get(endpoint, { params }).then((r) => r.data) });
  if (isLoading) return <Spin />;
  if (error) return <div className="text-red-600 p-4">{errOf(error)}</div>;
  const Row = ({ label, value, bold, indent, neg, note }) => (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? 'font-bold border-t mt-1 pt-2' : ''}`} style={{ paddingLeft: indent ? 16 : 0 }}>
      <span className={bold ? 'text-gray-900' : 'text-gray-600'}>{label}{note && <span className="text-xs text-gray-400"> · {note}</span>}</span>
      <span className={(neg ? -value : value) < 0 ? 'text-red-500' : ''}>{neg && value ? '−' : ''}{money(Math.abs(value))}</span>
    </div>
  );
  return (
    <Card className="p-5 max-w-2xl">
      <Row label="Sales (without VAT)" value={data.sales} />
      <Row label="Sales returns" value={data.returns} neg indent />
      <Row label="Net sales" value={data.net_sales} bold />
      <Row label="Cost of goods sold" value={data.cogs} neg indent />
      <Row label="Loss & damage" value={data.loss_damage} neg indent />
      <Row label="Stock counts & corrections" value={data.stock_adjustments} neg indent />
      <Row label={`Gross profit (${data.gross_margin}%)`} value={data.gross_profit} bold />
      {data.expenses.map((e) => <Row key={e.account} label={e.account.replace(/^\d+ · /, '')} value={e.amount} neg indent />)}
      <Row label="Total expenses" value={data.total_expenses} neg bold />
      {!!data.other_income && <Row label="Other income" value={data.other_income} indent />}
      {!!data.fx_differences && <Row label="Exchange rate differences" value={data.fx_differences} indent />}
      <div className={`flex justify-between mt-3 pt-3 border-t-2 text-lg font-extrabold ${data.net_profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}><span>Net profit</span><span>{money(data.net_profit)}</span></div>
      <div className="text-xs text-gray-400 mt-3">{data.note}{data.vat_collected ? ` VAT collected (owed to the state, not income): ${money(data.vat_collected)}.` : ''}</div>
    </Card>
  );
}

/* ── Chart of accounts + which account each payment method uses ─────── */
export function ChartTab() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ['acc-chart'], queryFn: () => api.get('/api/accounting/accounts').then((r) => r.data) });
  const [form, setForm] = useState({ code: '', name: '', type: 'expense' });
  if (isLoading) return <Spin />;
  const add = async () => {
    try { await api.post('/api/accounting/accounts', form); setForm({ code: '', name: '', type: 'expense' }); refetch(); } catch (e) { alert(errOf(e)); }
  };
  const rename = async (a) => { const name = window.prompt('New name', a.name); if (!name) return; try { await api.put(`/api/accounting/accounts/${a._id}`, { name }); refetch(); } catch (e) { alert(errOf(e)); } };
  const toggle = async (a) => { try { await api.put(`/api/accounting/accounts/${a._id}`, { active: !a.active }); refetch(); } catch (e) { alert(errOf(e)); } };
  const link = async (m, code) => { try { await api.put(`/api/accounting/payment-methods/${m._id}/account`, { account_code: code }); refetch(); } catch (e) { alert(errOf(e)); } };
  const assetAccounts = data.accounts.filter((a) => a.type === 'asset' && a.active);
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500"><tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th><th></th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {data.accounts.map((a) => (
              <tr key={a._id} className={a.active ? '' : 'opacity-50'}>
                <td className="px-3 py-1.5 font-mono">{a.code}</td><td className="px-3 py-1.5">{a.name}{a.system && <span className="ml-2 text-[10px] bg-gray-100 text-gray-500 rounded px-1">system</span>}</td>
                <td className="px-3 py-1.5 text-gray-500">{a.type}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => rename(a)} className="text-xs text-blue-600 mr-2">Rename</button>
                  {!a.system && <button onClick={() => toggle(a)} className="text-xs text-gray-500">{a.active ? 'Hide' : 'Show'}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="space-y-4">
        <Card className="p-4">
          <div className="font-bold mb-2">New account</div>
          <input placeholder="Code (4 digits)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="border rounded-lg px-3 py-1.5 w-full mb-2 text-sm" />
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border rounded-lg px-3 py-1.5 w-full mb-2 text-sm" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border rounded-lg px-3 py-1.5 w-full mb-2 text-sm">
            {['asset', 'liability', 'equity', 'income', 'expense'].map((t) => <option key={t}>{t}</option>)}
          </select>
          <button onClick={add} className="w-full py-2 rounded-lg text-white font-semibold" style={{ background: GREEN }}>Add</button>
          <p className="text-xs text-gray-400 mt-2">1xxx assets · 2xxx liabilities · 3xxx equity · 4xxx income · 5xxx–7xxx expenses</p>
        </Card>
        <Card className="p-4">
          <div className="font-bold mb-2">Payment methods → account</div>
          {data.payment_methods.map((m) => (
            <div key={m._id} className="flex items-center gap-2 mb-2 text-sm">
              <span className="flex-1">{m.name} <span className="text-gray-400 text-xs">({m.type})</span></span>
              <select value={m.account_code || ''} onChange={(e) => link(m, e.target.value)} className="border rounded-lg px-2 py-1 text-sm">
                <option value="">automatic</option>
                {assetAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
              </select>
            </div>
          ))}
          <p className="text-xs text-gray-400">“Automatic”: cash goes to 1000 · Cash, every bank / OMT / Whish / card method gets its own account.</p>
        </Card>
      </div>
    </div>
  );
}

/* ── Audit trail ────────────────────────────────────────────────────── */
export function AuditTab({ params }) {
  const { data = [], isLoading } = useQuery({ queryKey: ['audit', params], queryFn: () => api.get('/api/audit', { params: { ...params, limit: 500 } }).then((r) => r.data) });
  if (isLoading) return <Spin />;
  const show = (o) => (o ? Object.entries(o).filter(([k]) => !['_id', 'updatedAt', 'createdAt'].includes(k)).map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? JSON.stringify(v).slice(0, 60) : v}`).join(' · ') : '');
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth: 760 }}>
        <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Who</th><th className="text-left px-3 py-2">What</th><th className="text-left px-3 py-2">Before → After</th></tr></thead>
        <tbody className="divide-y divide-gray-100">
          {!data.length && <tr><td colSpan={4} className="text-center text-gray-400 py-10">Nothing recorded in this period.</td></tr>}
          {data.map((a) => (
            <tr key={a._id}>
              <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">{new Date(a.at).toLocaleString()}</td>
              <td className="px-3 py-1.5">{a.user} <span className="text-xs text-gray-400">{a.role}</span></td>
              <td className="px-3 py-1.5">{a.action}{a.name ? ` · ${a.name}` : ''}{a.line ? ` · ${a.line}` : ''}</td>
              <td className="px-3 py-1.5 text-xs text-gray-600">{show(a.before)}{a.after ? ` → ${show(a.after)}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ── Lock date (closed periods) ─────────────────────────────────────── */
export function LockDateControl({ settings }) {
  const qc = useQueryClient();
  const [val, setVal] = useState(settings?.accounting?.lock_date ? String(settings.accounting.lock_date).slice(0, 10) : '');
  const save = async (v) => {
    try { await api.put('/api/settings', { accounting: { ...(settings.accounting || {}), lock_date: v || null } }); qc.invalidateQueries(['settings']); setVal(v); }
    catch (e) { alert(errOf(e)); }
  };
  return (
    <div className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5" title="Nothing dated on or before this day can be added, changed, cancelled or deleted.">
      <span>🔒 Books closed up to</span>
      <input type="date" value={val} onChange={(e) => setVal(e.target.value)} className="border rounded px-2 py-0.5" />
      <button onClick={() => save(val)} className="text-emerald-700 font-semibold">Save</button>
      {val && <button onClick={() => save('')} className="text-gray-400">Open</button>}
    </div>
  );
}
